import { useEffect, useRef, useState } from "react";
import { Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/components/map/maplibreWorker"; // pins the MapLibre worker URL once
import { arc, boundsOf } from "@/components/map/mapUtils";
import { MapPin, Warehouse, ExternalLink } from "lucide-react";
import { num } from "@/lib/format";
import { cn } from "@/lib/utils";

const KARTVERKET = "https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png";

/**
 * Where the rig goes: Kartverket gråtone (public tiles, © Kartverket), the site, the depot and the planned
 * origin → destination as a dashed arc. Straight-line distance only – no route or GPS claims.
 */
export const ProposalSiteMap = ({ logistics, className, height = 320 }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const site = logistics?.site;
  const depot = logistics?.depot;

  useEffect(() => {
    if (!ref.current || mapRef.current || !site) return undefined;
    const pts = [site, depot].filter(Boolean);
    const bounds = boundsOf(pts, 0.12);
    const m = new MapLibreMap({
      container: ref.current,
      style: {
        version: 8,
        sources: { kv: { type: "raster", tiles: [KARTVERKET], tileSize: 256, maxzoom: 18, attribution: "© Kartverket" } },
        layers: [
          { id: "bg", type: "background", paint: { "background-color": "#eef1f5" } },
          { id: "kv", type: "raster", source: "kv", paint: { "raster-opacity": 0.9, "raster-contrast": -0.1, "raster-brightness-min": 0.05, "raster-saturation": -0.6 } },
        ],
      },
      bounds,
      fitBoundsOptions: { padding: { top: 48, bottom: 130, left: 64, right: 64 } },
      attributionControl: false,
      interactive: false,
      fadeDuration: 150,
    });
    m.on("load", () => {
      if (depot) {
        m.addSource("leg", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: arc(depot, site, 0.16) } } });
        m.addLayer({ id: "leg-halo", type: "line", source: "leg", paint: { "line-color": "#ffffff", "line-width": 6, "line-opacity": 0.8 } });
        m.addLayer({ id: "leg", type: "line", source: "leg", paint: { "line-color": "#2670F2", "line-width": 2.2, "line-dasharray": [2, 2] } });
      }
      const mk = (el, p) => new Marker({ element: el, anchor: "center" }).setLngLat([p.lng, p.lat]).addTo(m);
      if (depot) {
        const d = document.createElement("div");
        d.className = "proposal-map-depot";
        d.innerHTML = '<span class="proposal-map-depot-dot"></span><span class="proposal-map-label">Depot</span>';
        mk(d, depot);
      }
      const s = document.createElement("div");
      s.className = "proposal-map-site";
      s.innerHTML = '<span class="proposal-map-pulse"></span><span class="proposal-map-site-dot"></span><span class="proposal-map-label proposal-map-label-strong">Leveringssted</span>';
      mk(s, site);
      setReady(true);
    });
    m.on("error", (e) => { if (e?.error?.status) return; });
    mapRef.current = m;
    return () => { m.remove(); mapRef.current = null; };
  }, [site, depot]);

  if (!site) return null;
  const gmaps = `https://www.google.com/maps/search/?api=1&query=${site.lat},${site.lng}`;

  return (
    <figure className={cn("relative overflow-hidden rounded-3xl bg-muted ring-1 ring-border/70", className)} style={{ height }} data-testid="proposal-site-map" data-ready={ready ? "1" : "0"}>
      <div ref={ref} className="h-full w-full [&_.maplibregl-canvas]:outline-none" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-background/95 via-background/60 to-transparent" aria-hidden="true" />
      <figcaption className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[15px] font-medium"><MapPin className="h-4 w-4 text-primary" /> <span className="truncate" data-testid="proposal-site-map-address">{site.name}</span></div>
          {depot && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Warehouse className="h-3.5 w-3.5" /> Fra {depot.name}</span>
              {logistics.distance_km != null && <span className="font-mono tabular">ca. {num(logistics.distance_km)} km luftlinje</span>}
              {logistics.transports ? <span className="font-mono tabular">{num(logistics.transports)} transporter planlagt</span> : null}
            </div>
          )}
        </div>
        <a href={gmaps} target="_blank" rel="noopener noreferrer" className="pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-card/90 px-3 text-[12.5px] font-medium ring-1 ring-border backdrop-blur transition-colors duration-150 hover:bg-card" data-testid="proposal-site-map-open">
          Åpne i kart <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </figcaption>
      <span className="absolute right-3 top-3 rounded-md bg-card/80 px-1.5 py-0.5 text-[10.5px] text-muted-foreground backdrop-blur">© Kartverket</span>
    </figure>
  );
};
